import { Copy, Eye, FileText, Plus, Search } from "lucide-react";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../../api/client";
import {
	Button,
	Input,
	Modal,
	Notification,
	Table,
	useNotification,
} from "../../components/FormComponents";
import Pagination from "../../components/Pagination";
import { useCrudPage } from "../../hooks/useCrudPage";
import { clientSchema } from "../../schemas/clientSchema";

export default function Clients() {
	const navigate = useNavigate();
	const { notification, showNotification } = useNotification();

	// Utiliza o hook centralizado para toda a lógica de CRUD
	const {
		data: clients,
		loading,
		saving,
		page,
		limit,
		total,
		search,
		isModalOpen,
		editingId,
		formData,
		errors,
		setPage,
		setSearch,
		updateField,
		handleOpenModal,
		handleCloseModal,
		handleSubmit,
		handleDelete,
	} = useCrudPage("/clients", clientSchema, {
		onSuccess: (action) => {
			const messages = {
				created: "Cliente criado com sucesso!",
				updated: "Cliente atualizado com sucesso!",
				deleted: "Cliente deletado com sucesso!",
			};
			showNotification(messages[action]);
		},
	});

	// Lógica específica de Tokens que não faz parte do CRUD padrão
	const [showTokens, setShowTokens] = useState(null);
	const [tokens, setTokens] = useState([]);
	const [savingToken, setSavingToken] = useState(false);
	const [copiedToken, setCopiedToken] = useState(null);

	const fetchTokens = useCallback(
		async (clientId) => {
			try {
				const response = await client.get(`/clients/${clientId}/tokens`);
				setTokens(response.data);
				setShowTokens(clientId);
			} catch (error) {
				console.error("Error fetching tokens:", error);
				showNotification("Erro ao carregar tokens", "error");
			}
		},
		[showNotification],
	);

	const handleGenerateToken = async (clientId) => {
		try {
			setSavingToken(true);
			await client.post(`/clients/${clientId}/generate-token`);
			await fetchTokens(clientId);
			showNotification("Token gerado com sucesso!");
		} catch (error) {
			console.error("Error generating token:", error);
			showNotification("Erro ao gerar token", "error");
		} finally {
			setSavingToken(false);
		}
	};

	const copyToClipboard = (token) => {
		navigator.clipboard.writeText(token);
		setCopiedToken(token);
		setTimeout(() => setCopiedToken(null), 2000);
	};

	const columns = [
		{ key: "name", label: "Nome" },
		{ key: "email", label: "Email" },
		{ key: "phone", label: "Telefone" },
		{ key: "cnpj", label: "CNPJ" },
	];

	return (
		<div className="space-y-6">
			<Notification notification={notification} />

			{/* Header */}
			<div className="flex items-center justify-between">
				<h1 className="text-2xl font-bold text-white">Gerenciar Clientes</h1>
				<Button type="button" onClick={() => handleOpenModal()}>
					<Plus className="w-4 h-4 inline mr-2" />
					Novo Cliente
				</Button>
			</div>

			{/* Search */}
			<div className="relative">
				<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
				<input
					type="text"
					value={search}
					onChange={(e) => {
						setSearch(e.target.value);
						setPage(1);
					}}
					placeholder="Buscar clientes..."
					className="w-full pl-10 pr-4 py-2.5 bg-slate-900/50 border border-white/10 rounded-lg text-slate-300 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/30 transition-all"
				/>
			</div>

			{/* Table Content */}
			<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6">
				<Table
					columns={columns}
					data={clients}
					loading={loading}
					onEdit={handleOpenModal}
					onDelete={(id) =>
						handleDelete(id, "Tem certeza que deseja deletar este cliente?")
					}
					actions={(row) => (
						<>
							<button
								type="button"
								onClick={() => navigate(`/admin/clients/${row.id}`)}
								className="px-3 py-1 bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-300 rounded text-xs transition-all active:scale-95 flex items-center gap-1"
							>
								<Eye className="w-3 h-3" /> Ver
							</button>
							<button
								type="button"
								onClick={() => fetchTokens(row.id)}
								className="px-3 py-1 bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 rounded text-xs transition-all active:scale-95 flex items-center gap-1"
							>
								<FileText className="w-3 h-3" /> Tokens
							</button>
						</>
					)}
				/>

				{/* Pagination Component Profissional */}
				<Pagination
					page={page}
					limit={limit}
					total={total}
					onPageChange={setPage}
				/>
			</div>

			{/* Form Modal */}
			<Modal
				isOpen={isModalOpen}
				title={editingId ? "Editar Cliente" : "Novo Cliente"}
				onClose={handleCloseModal}
				onSubmit={handleSubmit}
				submitLabel={editingId ? "Atualizar" : "Criar"}
				loading={saving}
			>
				<div className="space-y-4">
					<Input
						label="Nome *"
						value={formData.name || ""}
						onChange={(e) => updateField("name", e.target.value)}
						placeholder="Nome do cliente"
						error={errors.name}
					/>
					<Input
						label="Email *"
						type="email"
						value={formData.email || ""}
						onChange={(e) => updateField("email", e.target.value)}
						placeholder="email@cliente.com"
						error={errors.email}
					/>
					<Input
						label="Telefone"
						value={formData.phone || ""}
						onChange={(e) => updateField("phone", e.target.value)}
						placeholder="+55 (11) 9999-9999"
						error={errors.phone}
					/>
					<Input
						label="CNPJ"
						value={formData.cnpj || ""}
						onChange={(e) => updateField("cnpj", e.target.value)}
						placeholder="00.000.000/0000-00"
						error={errors.cnpj}
					/>
					<Input
						label="Endereco"
						value={formData.address || ""}
						onChange={(e) => updateField("address", e.target.value)}
						placeholder="Endereco completo"
						error={errors.address}
					/>
					{errors.submit && (
						<p className="text-sm text-red-500 bg-red-500/10 p-2 rounded border border-red-500/20">
							{errors.submit}
						</p>
					)}
				</div>
			</Modal>

			{/* Tokens Modal */}
			{showTokens && (
				<Modal
					isOpen={true}
					title="Tokens do Cliente"
					onClose={() => {
						setShowTokens(null);
						setTokens([]);
					}}
					onSubmit={() => handleGenerateToken(showTokens)}
					submitLabel="Gerar Novo Token"
					loading={savingToken}
				>
					<div className="space-y-3">
						{tokens.length === 0 ? (
							<div className="flex flex-col items-center justify-center py-8 text-center">
								<FileText className="w-8 h-8 text-slate-400 mb-2" />
								<p className="text-slate-400">Nenhum token gerado ainda</p>
								<p className="text-xs text-slate-500 mt-2">
									Clique em "Gerar Novo Token" para criar um
								</p>
							</div>
						) : (
							<div className="space-y-2 max-h-64 overflow-y-auto">
								{tokens.map((token, idx) => (
									<div
										key={token.id || idx}
										className="flex items-center justify-between p-3 bg-slate-800/50 rounded border border-slate-700 hover:border-slate-600 transition-all"
									>
										<div className="flex-1 min-w-0">
											<code className="text-xs text-cyan-300 truncate block font-mono">
												{token.token}
											</code>
											<p className="text-xs text-slate-500 mt-1">
												{token.revoked ? "Revogado" : "Ativo"}
											</p>
										</div>
										{!token.revoked && (
											<button
												type="button"
												onClick={() => copyToClipboard(token.token)}
												className={`ml-2 p-2 rounded transition-all ${
													copiedToken === token.token
														? "bg-green-600/30 text-green-300"
														: "text-slate-400 hover:text-slate-200 hover:bg-slate-700/50"
												}`}
											>
												<Copy className="w-4 h-4" />
											</button>
										)}
									</div>
								))}
							</div>
						)}
						<div className="mt-4 p-3 bg-blue-600/10 border border-blue-500/20 rounded text-xs text-blue-300">
							Compartilhe o token com o cliente para que ele acesse suas
							propostas
						</div>
					</div>
				</Modal>
			)}
		</div>
	);
}
